import {
  trackRemoteIntroPromoClicked,
  trackRemoteIntroPromoDismissed,
  trackRemoteIntroPromoViewed,
} from '@/models/analytics/features/trackRemoteIntroPromoAnalytics';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  remoteIntroPromoModalEntrySurfaceAtom,
  remoteIntroPromoModalOpenAtom,
} from '../atoms/remote-intro-promo-atoms';
import { useRemoteIntroPromoEligibility } from '../eligibility/useIsRemoteIntroPromoEligible';
import { remoteIntroPromoMessages, remoteIntroPromoSharedMessages } from '../messages';
import { useRemoteIntroPromoSeen } from '../useRemoteIntroPromoSeen';

const SURFACE = 'profile_menu' as const;

interface RemoteIntroPromoMenuCardProps {
  /** Optional callback fired after the user clicks the See-offer CTA. */
  onAfterClick?: () => void;
}

/**
 * Cohort-aware promo card rendered inside the user-avatar dropdown. Layout
 * matches Figma node 11164-15151: header row (title + green Offer badge +
 * close X), a single-line caption, and a secondary "See offer" button.
 * Seen-state lives on the backend via `ModalId.REMOTE_INTRO_PROMO` —
 * dismissing the card also hides the Billing banner and survives across
 * devices.
 */
export const RemoteIntroPromoMenuCard = ({
  onAfterClick,
}: RemoteIntroPromoMenuCardProps) => {
  const intl = useIntl();
  const cohort = useRemoteIntroPromoEligibility();
  const setModalOpen = useSetAtom(remoteIntroPromoModalOpenAtom);
  const setEntrySurface = useSetAtom(remoteIntroPromoModalEntrySurfaceAtom);
  const { isSeen, markDismissed } = useRemoteIntroPromoSeen();

  const visible = cohort !== null && !isSeen;
  // Tracks the last cohort we emitted `viewed` for — so a cohort transition
  // (rare but possible: trial expires, settings change) emits a fresh
  // impression instead of swallowing it as "already tracked this mount".
  const lastTrackedCohortRef = useRef<typeof cohort>(null);

  useEffect(() => {
    if (visible && cohort && lastTrackedCohortRef.current !== cohort) {
      lastTrackedCohortRef.current = cohort;
      trackRemoteIntroPromoViewed(SURFACE, cohort);
    }
  }, [visible, cohort]);

  if (!visible || !cohort) return null;
  const messages = remoteIntroPromoMessages[cohort];

  const handleCtaClick = () => {
    trackRemoteIntroPromoClicked(SURFACE, cohort);
    setEntrySurface(SURFACE);
    setModalOpen(true);
    onAfterClick?.();
  };

  const handleDismiss = () => {
    trackRemoteIntroPromoDismissed(SURFACE, cohort);
    markDismissed();
  };

  return (
    <div
      // `bg-surface-elevated` matches the DropdownMenuContent surface the
      // card lives inside — `bg-surface` (used by cards / inline content)
      // flips to a different shade in dark mode and reads as a darker tile
      // against the popover.
      className="flex w-full flex-col gap-2 border-t border-border bg-surface-elevated py-4"
      data-testid="remote-intro-promo-menu-card"
      data-cohort={cohort}
    >
      <div className="flex items-center gap-1 px-4">
        {/* `truncate` is kept as a safety net — the surrounding popover is
            widened to fit the longest cohort title, but if future copy grows
            past that floor we prefer truncation over wrapping the title row. */}
        <TypographyP2SemiBold className="min-w-0 truncate text-text-primary">
          <FormattedMessage {...messages.menuCardTitle} />
        </TypographyP2SemiBold>
        <Badge
          color="green"
          size="xs"
          className="rounded"
          data-testid="remote-intro-promo-menu-card-badge"
        >
          <FormattedMessage
            defaultMessage="Offer"
            id="P2NCzVfKFJ"
            description="Green Offer badge inline next to the menu-card title"
          />
        </Badge>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          iconOnly
          onClick={handleDismiss}
          aria-label={intl.formatMessage(remoteIntroPromoSharedMessages.dismissAriaLabel)}
          data-testid="remote-intro-promo-menu-card-close"
          className="ms-auto text-text-secondary hover:bg-transparent hover:text-text-primary"
        >
          <X />
        </Button>
      </div>
      <TypographyCaption className="px-4 text-text-secondary">
        <FormattedMessage {...messages.menuCardDescription} />
      </TypographyCaption>
      <div className="px-4 pt-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleCtaClick}
          data-testid="remote-intro-promo-menu-card-cta"
          className="w-full"
        >
          <FormattedMessage
            defaultMessage="See offer"
            id="Vmg6xDWyC8"
            description="Primary CTA label on the profile-menu card"
          />
        </Button>
      </div>
    </div>
  );
};
